import React, { useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';

import styles from './prepaidCard.module.scss';
import CardImage from './CardImage';
import CardInformation from './CardInformation';
import { fetchCardData, fetchSensitiveCardData, reportMisuse, getAvailableBalanceStr, getCardData, getFetchCardDataError, getFetchSensitiveCardDataError, getIsFetchingCardData, getIsFetchingSensitiveCardData, getSensitiveCardData, getSpentAmountStr, getIsReportingMisuse, getReportMisuseError } from 'redux/modules/prepaidCard';
import { compose, pathOr } from 'ramda';
import { connect } from 'react-redux';
import { ErrorAlert, Loader } from '@huggg/react-components';
import CancelledCard from './CancelledCard';

import { CardRestrictionDetails } from './CardRestrictionDetails';

const getCardTitle = (segment) => {
  return pathOr('Card', ['configuration', 'sangriaLayout', 'prepaidCard', 'title'], segment);
};

const PrepaidCard = props => {
  const {
    id,
    isFetchingCardData,
    fetchCardData,
    fetchCardDataError,
    availableBalance,
    spentAmount,
    sensitiveCardData,
    isFetchingSensitiveCardData,
    fetchSensitiveCardDataError,
    fetchSensitiveCardData,
    reportMisuse,
    isReportingMisuse,
    reportMisuseError,
    cardData,
    segment,
  } = props;

  const cardTitle = getCardTitle(segment);

  const helpLinkToShow = useMemo(() => {
    return ['energy', 'water'].find(keyword =>
      cardTitle.toLowerCase().includes(keyword)
    ) ?? null;
  }, [cardTitle]);

  useEffect(() => {
    fetchCardData(id);
  }, [fetchCardData, id]);

  if (isFetchingCardData) {
    return <div className={styles.container}><Loader /></div>;
  }

  if (fetchCardDataError || cardData?.status === 'pending') {
    return <div className={styles.container}><ErrorAlert message="There was an error fetching your card, please refresh and try again." /></div>;
  }

  if (!cardData || cardData.status !== 'active') {
    return <div className={styles.container}><CancelledCard /></div>;
  }

  return (
    <div className={styles.container}>
      <CardImage cardData={cardData} cardTitle={cardTitle} />

      <CardRestrictionDetails helpLinkToShow={helpLinkToShow} />

      <CardInformation
        cardTitle={cardTitle}
        availableBalance={availableBalance}
        spentAmount={spentAmount}
        fetchSensitiveCardData={fetchSensitiveCardData}
        sensitiveCardData={sensitiveCardData}
        isFetchingSensitiveCardData={isFetchingSensitiveCardData}
        fetchSensitiveCardDataError={fetchSensitiveCardDataError}
        reportMisuse={reportMisuse}
        isReportingMisuse={isReportingMisuse}
        reportMisuseError={reportMisuseError}
      />
    </div>
  );
};

PrepaidCard.propTypes = {
  id: PropTypes.string.isRequired,
  cardData: PropTypes.object,
  isFetchingCardData: PropTypes.bool.isRequired,
  fetchCardDataError: PropTypes.object,
  fetchCardData: PropTypes.func.isRequired,
  availableBalance: PropTypes.string.isRequired,
  spentAmount: PropTypes.string.isRequired,
  sensitiveCardData: PropTypes.object,
  isFetchingSensitiveCardData: PropTypes.bool.isRequired,
  fetchSensitiveCardDataError: PropTypes.object,
  fetchSensitiveCardData: PropTypes.func.isRequired,
  reportMisuse: PropTypes.func.isRequired,
  isReportingMisuse: PropTypes.bool.isRequired,
  reportMisuseError: PropTypes.object,
  segment: PropTypes.object,
};

const mapStateToProps = state => ({
  cardData: getCardData(state),
  availableBalance: getAvailableBalanceStr(state),
  spentAmount: getSpentAmountStr(state),
  isFetchingCardData: getIsFetchingCardData(state),
  fetchCardDataError: getFetchCardDataError(state),
  sensitiveCardData: getSensitiveCardData(state),
  isFetchingSensitiveCardData: getIsFetchingSensitiveCardData(state),
  fetchSensitiveCardDataError: getFetchSensitiveCardDataError(state),
  isReportingMisuse: getIsReportingMisuse(state),
  reportMisuseError: getReportMisuseError(state),
});

export default compose(
  connect(mapStateToProps, { fetchCardData, fetchSensitiveCardData, reportMisuse })
)(PrepaidCard);
